import { IAdAdapter } from "./IAdAdapter";

declare class AdexiumWidget {
  constructor(config: { wid: string; adFormat: string });
  requestAd(format: string, motivated?: boolean): Promise<any>;
  displayAd(ads: any, format?: string): void;
  on(event: string, callback: () => void): void;
  off(event: string, callback: () => void): void;
}

export class AdexiumAdapter implements IAdAdapter {
  readonly name = "adexium";
  private widget: AdexiumWidget | null = null;
  private pendingAds: any = null;

  constructor(private blockId: string) {}

  isAvailable(): boolean {
    return !!(window as any).AdexiumWidget;
  }

  async requestAd(type: "rewarded" | "interstitial"): Promise<boolean> {
    const AW = (window as any).AdexiumWidget;
    if (!AW) throw new Error("SDK not loaded");
    this.widget = new AW({ wid: this.blockId, adFormat: "interstitial" });
    this.pendingAds = await this.widget!.requestAd("interstitial", type === "rewarded");
    if (!this.pendingAds) throw new Error("no fill");
    return true;
  }

  async showAd(): Promise<boolean> {
    return this.display(true) as Promise<boolean>;
  }

  async showAdInterstitial(): Promise<void> {
    await this.display(false);
  }

  private display(rewarded: boolean): Promise<boolean | void> {
    if (!this.widget || !this.pendingAds) return Promise.reject(new Error("no pending ad"));
    const widget = this.widget;
    const ads = this.pendingAds;
    this.pendingAds = null;

    return new Promise((resolve, reject) => {
      let resolved = false;
      let observer: MutationObserver | null = null;
      let domChanged = false;

      const cleanup = () => {
        widget.off("adPlaybackCompleted", onOk);
        widget.off("adClosed", onOk);
        widget.off("requestAdError", onFail);
        if (observer) { observer.disconnect(); observer = null; }
      };

      const succeed = () => {
        if (resolved) return;
        resolved = true;
        cleanup();
        resolve(rewarded ? true : undefined);
      };

      const fail = (err?: string) => {
        if (resolved) return;
        resolved = true;
        cleanup();
        reject(new Error(err || "adexium display failed"));
      };

      const onOk = () => succeed();
      const onFail = () => fail("widget error");

      widget.on("adPlaybackCompleted", onOk);
      widget.on("adClosed", onOk);
      widget.on("requestAdError", onFail);

      // MutationObserver: detect ad overlay lifecycle
      observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.addedNodes.length > 0) domChanged = true;
          if (domChanged && m.removedNodes.length > 0) {
            for (const n of Array.from(m.removedNodes)) {
              if (n instanceof HTMLElement) { succeed(); return; }
            }
          }
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });

      widget.displayAd(ads, "interstitial");

      // 3s: if displayAd produced no DOM changes → REJECT (not resolve)
      // so AdManager waterfall tries next adapter
      setTimeout(() => {
        if (!domChanged && !resolved) fail("no DOM change after displayAd");
      }, 3000);

      // Safety: if ad rendered but callbacks never fire
      setTimeout(() => {
        if (!resolved) fail("display timeout");
      }, rewarded ? 45000 : 30000);
    });
  }
}
